import { v4 as uuid } from 'uuid';
import { jwtDecode } from 'jwt-decode';
import { FeatureFlagsType } from '../constants/featureFlags';

const FEATURE_FLAGS_KEY = 'featureFlags';
export const TOKEN_KEY = 'screenapp_token';
const LAST_USED_LOGIN_METHOD_KEY = 'lastUsedLoginMethod';

export type LoginMethod = 'google' | 'apple' | 'facebook' | 'email' | 'passkey';

export const setLastUsedLoginMethod = (method: LoginMethod) => {
  localStorage.setItem(LAST_USED_LOGIN_METHOD_KEY, method);
};

export const getLastUsedLoginMethod = (): LoginMethod | null => {
  const method = localStorage.getItem(LAST_USED_LOGIN_METHOD_KEY);
  if (
    method === 'google' ||
    method === 'apple' ||
    method === 'facebook' ||
    method === 'email' ||
    method === 'passkey'
  ) {
    return method;
  }
  return null;
};

export const setFeatureFlags = (data: FeatureFlagsType) => {
  localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(data));
};

export const loadFeatureFlags = (): FeatureFlagsType => {
  const featureFlags = localStorage.getItem(FEATURE_FLAGS_KEY);
  return featureFlags ? JSON.parse(featureFlags) : {};
};

// In-memory cache for token expiry to avoid repeated JWT decoding
let cachedTokenExpiry: number | null = null;
let cachedToken: string | null = null;

// Function to clear cache - useful for testing
export const clearTokenCache = () => {
  cachedToken = null;
  cachedTokenExpiry = null;
};

export const getToken = () => {
  if (cachedToken) {
    return cachedToken;
  }

  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    cachedToken = token;
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      if (decoded.exp) {
        const expiry = decoded.exp * 1000;
        cachedTokenExpiry = expiry;
      } else {
        cachedTokenExpiry = null; // Clear expiry cache if token has no expiry
      }
    } catch (error) {
      console.warn('Failed to decode JWT token when getting:', error);
      cachedTokenExpiry = null;
    }
  }

  return cachedToken;
};

// Making a private function to make sure we don't have a race-condition when getting token from browser storage
const _getTokenFromBrowserStorageOnly = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      if (decoded.exp) {
        // It is valid token, so we can return it
        return token;
      }
    } catch (error) {
      console.warn(
        'Failed to decode browser storage JWT token when getting:',
        error,
      );
    }
  }

  return null;
};

const MINUTE = 1000 * 60;
const DAY = MINUTE * 60 * 24;

const getTokenExpiry = (token?: string): number | null => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return null;

  // Use cache if we're checking the same token
  if (tokenToCheck === cachedToken && cachedTokenExpiry !== null) {
    return cachedTokenExpiry;
  }

  try {
    const decoded = jwtDecode<{ exp: number }>(tokenToCheck);
    if (decoded.exp) {
      const expiry = decoded.exp * 1000; // Convert to milliseconds
      // Update cache
      cachedToken = tokenToCheck;
      cachedTokenExpiry = expiry;
      return expiry;
    }
    return null;
  } catch (error) {
    console.warn('Failed to decode JWT token for expiry:', error);
    return null;
  }
};

export const shouldRefreshToken = () => {
  const token = getToken();
  if (!token) return false;

  const expiry = getTokenExpiry(token);
  if (!expiry) return false;

  const now = Date.now();
  // Refresh tokens expiring within the next day, but not the already-expired ones.
  return expiry <= now + DAY && expiry > now;
};

// Note This function is the decision-maker to update the browser storage with a new token
// Edge case: When using both memory cache and browser storage to make a decision, it may return wrong decision if memory cache is not in sync with browser storage
export const isNewerThanStoredToken = (token: string): boolean => {
  const storedToken = getToken();
  if (!storedToken) return true; // Any token is better than no token

  const storedTokenFromBrowserStorageOnly = _getTokenFromBrowserStorageOnly();
  if (!storedTokenFromBrowserStorageOnly) {
    // Clearly browser storage does not have a token, update it if the new token is valid
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      if (decoded.exp) {
        return true;
      }
    } catch (error) {
      console.warn('Failed to decode new token:', error);
    }
  }

  const tokenExpiry = getTokenExpiry(token);
  const storedExpiry = getTokenExpiry(storedToken);

  // If we can't decode either token, don't replace
  if (!tokenExpiry && !storedExpiry) return false;
  if (!tokenExpiry) return false; // Don't replace valid stored token with invalid one
  if (!storedExpiry) return true; // Replace invalid stored token with valid one

  // Return true if new token expires later than stored token
  return tokenExpiry > storedExpiry;
};

export const setToken = (token: string, store: boolean = true) => {
  if (token && token.trim() !== '') {
    // Always update in-memory cache
    cachedToken = token;

    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      if (decoded.exp) {
        cachedTokenExpiry = decoded.exp * 1000;
      } else {
        // Clear expiry cache if token has no expiry
        cachedTokenExpiry = null;
      }
    } catch (error) {
      console.warn('Failed to decode JWT token when setting:', error);
      cachedTokenExpiry = null;
    }

    // Only store in localStorage if store=true
    if (store) {
      window.localStorage.setItem(TOKEN_KEY, token);
    }
  }
};

export const removeToken = (store: boolean = true) => {
  // Always clear in-memory cache
  clearTokenCache();

  // Only remove from localStorage if store=true
  if (store) {
    window.localStorage.removeItem(TOKEN_KEY);
  }
};

export const isTokenExpired = (token?: string) => {
  const tokenToCheck = token || getToken();
  if (!tokenToCheck) return true;

  const expiry = getTokenExpiry(tokenToCheck);
  if (!expiry) {
    // If we can't get expiry from token, consider it expired for safety
    return true;
  }

  return Date.now() >= expiry;
};

export const getOrCreateLocalTrackingId = () => {
  const ltidStored = localStorage.getItem('ltid');
  if (ltidStored === null) {
    const ltid = uuid();
    localStorage.setItem('ltid', ltid);
    return ltid;
  }

  return ltidStored;
};

/**
 * Comprehensive cache clearing function for signout
 * Clears all browser storage except theme preferences
 */
export const clearAllCacheExceptTheme = async (): Promise<void> => {
  try {
    // Preserve theme setting before clearing
    const darkModePreference = localStorage.getItem('darkMode');

    // Clear all localStorage except theme
    const keysToPreserve = ['darkMode'];
    const allKeys = Object.keys(localStorage);

    allKeys.forEach((key) => {
      if (!keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Restore theme preference
    if (darkModePreference !== null) {
      localStorage.setItem('darkMode', darkModePreference);
    }

    // Clear sessionStorage completely
    sessionStorage.clear();

    // Clear browser cache and service worker cache if available
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );
    }

    // Clear in-memory cache
    clearTokenCache();

    console.log(
      'Successfully cleared all cache except theme preferences and IndexedDB',
    );
  } catch (error) {
    console.error('Error clearing cache:', error);
    // Even if some clearing fails, we should still clear what we can
    // Clear localStorage manually as fallback
    const darkModePreference = localStorage.getItem('darkMode');
    localStorage.clear();
    if (darkModePreference !== null) {
      localStorage.setItem('darkMode', darkModePreference);
    }
    sessionStorage.clear();
    clearTokenCache();
  }
};
